import React from 'react'
import { LogOut } from 'lucide-react'
import { useAuth } from './AuthContext'

export interface UserBadgeProps {
  className?: string
  showLogout?: boolean
  variant?: 'pill' | 'card' | 'minimal'
}

const getDisplayInitials = (u: { displayName?: string; initials?: string; isGuest?: boolean }) => {
  if (u.initials) return u.initials.toUpperCase()
  if (u.isGuest && u.displayName) {
    const parts = u.displayName.trim().split(/\s+/)
    if (parts.length >= 2) {
      return parts[parts.length - 1].charAt(0).toUpperCase()
    }
    return u.displayName.trim().charAt(0).toUpperCase()
  }
  return u.initials || '?'
}

export const UserBadge: React.FC<UserBadgeProps> = ({
  className = '',
  showLogout = true,
  variant = 'pill',
}) => {
  const { user, logout } = useAuth()

  if (!user) return null

  // Minimal variant – avatar only
  if (variant === 'minimal') {
    return (
      <div
        className={`p-0.5 rounded-full bg-white text-neutral-900 shadow-sm select-none flex items-center justify-center ${className}`}
      >
        {/* Avatar */}
        {user.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.displayName}
            referrerPolicy="no-referrer"
            className="w-7 h-7 rounded-full object-cover shrink-0"
          />
        ) : (
          <div
            style={{ backgroundColor: user.color }}
            className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
          >
            {getDisplayInitials(user)}
          </div>
        )}
      </div>
    )
  }

  // Card variant – styled exactly like EditorDrawer menu items
  if (variant === 'card') {
    return (
      <div
        className={`w-full p-3.5 rounded-2xl border border-outline-lower hover:border-outline-base bg-surface transition-all flex items-center justify-between select-none ${className}`}
      >
        <div className="flex items-center gap-3.5 min-w-0">
          {/* Avatar (40px circular container matching icons in other menu items) */}
          {user.photoURL ? (
            <img
              src={user.photoURL}
              alt={user.displayName}
              referrerPolicy="no-referrer"
              className="w-10 h-10 rounded-full object-cover shrink-0 ring-1 ring-outline-lower"
            />
          ) : (
            <div
              style={{ backgroundColor: user.color }}
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-base shrink-0"
            >
              {getDisplayInitials(user)}
            </div>
          )}

          {/* User Info with typography matching Tags Library / Media Catalog */}
          <div className="min-w-0 flex flex-col">
            <div className="flex items-center gap-2">
              <span className="font-bold text-sm text-on-surface truncate">
                {user.displayName}
              </span>
              {user.isGuest && (
                <span
                  title={user.role === 'editor' ? 'Guest Editor' : 'Guest / Testing mode'}
                  className={`text-[9px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider shrink-0 border ${
                    user.role === 'editor'
                      ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30'
                      : 'bg-blue-500/15 text-blue-500 border-blue-500/30'
                  }`}
                >
                  {user.role === 'editor' ? 'Editor' : 'Guest'}
                </span>
              )}
            </div>
            <span className="text-xs text-secondaryGray700 dark:text-neutral-400 truncate mt-0.5">
              {user.isGuest ? 'Guest account' : user.email}
            </span>
          </div>
        </div>

        {/* Logout Action Button */}
        {showLogout && (
          <button
            type="button"
            onClick={logout}
            title="Sign out"
            className="p-2 rounded-xl text-on-surface-variant hover:text-red hover:bg-red/10 transition-colors cursor-pointer shrink-0 ml-2"
          >
            <LogOut className="w-4 h-4" />
          </button>
        )}
      </div>
    )
  }

  // Default pill variant – used for floating bottom-right badge in DashboardView
  return (
    <div
      className={`flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-surface/95 dark:bg-[#181B1D]/95 backdrop-blur-md border border-outline-lower shadow-lg shadow-black/5 dark:shadow-black/30 select-none ${className}`}
    >
      {/* Avatar (photoURL from Google or colored initials) */}
      {user.photoURL ? (
        <img
          src={user.photoURL}
          alt={user.displayName}
          referrerPolicy="no-referrer"
          className="w-7 h-7 rounded-full object-cover shrink-0 ring-1 ring-outline-lower"
        />
      ) : (
        <div
          style={{ backgroundColor: user.color }}
          className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0"
        >
          {getDisplayInitials(user)}
        </div>
      )}

      {/* User Info */}
      <div className="flex flex-col min-w-0 pr-1">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-semibold text-on-surface truncate leading-tight max-w-[140px]">
            {user.displayName}
          </span>
          {user.isGuest && (
            <span
              title={user.role === 'editor' ? 'Guest Editor' : 'Guest / Testing mode'}
              className={`text-[9px] px-1.5 py-0.2 rounded-full font-bold uppercase tracking-wider shrink-0 border ${
                user.role === 'editor'
                  ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/30'
                  : 'bg-blue-500/15 text-blue-500 border-blue-500/30'
              }`}
            >
              {user.role === 'editor' ? 'Editor' : 'Guest'}
            </span>
          )}
        </div>
        <span className="text-[10px] text-on-surface-variant/70 truncate leading-none mt-0.5 max-w-[140px]">
          {user.isGuest ? 'Guest account' : user.email}
        </span>
      </div>

      {/* Logout button */}
      {showLogout && (
        <button
          type="button"
          onClick={logout}
          title="Sign out"
          className="p-1.5 rounded-full text-on-surface-variant hover:text-red hover:bg-surface-variant transition-colors cursor-pointer ml-0.5"
        >
          <LogOut className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )
}

export default UserBadge
